import React from "react";
import { Link } from "react-router-dom";
import { UserCog, Moon, Sun, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useUserAllowedRoles } from "@/hooks/useUserAllowedRoles";
import { useHallOfFame } from "@/hooks/useHallOfFame";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import LanguageSelector from "./LanguageSelector";
import { cn } from "@/lib/utils";

interface MobileMenuProps {
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const { profile, updateRole } = useAuth();
  const { t } = useLanguage();
  const { currentUserRank } = useHallOfFame();
  const isTopThree = currentUserRank !== null && currentUserRank <= 3;
  const { mode, toggleMode } = useTheme();
  const { allowedRoles, loading: rolesLoading } = useUserAllowedRoles();
  const currentRole = profile?.role || "student";

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  const getRoleLabel = (role: "student" | "teacher" | "admin") => {
    switch (role) {
      case "student":
        return t("Žák", "Žiak");
      case "teacher":
        return t("Učitel", "Učiteľ");
      case "admin":
        return t("Správce", "Správca");
      default:
        return t("Role", "Rola");
    }
  };

  const handleRoleChange = (role: "student" | "teacher" | "admin") => {
    if (profile && role !== currentRole) {
      updateRole(role);
      setIsMenuOpen(false);
    }
  };

  return (
    <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle>{t("Navigace", "Navigácia")}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-2">
          {/* Navigace pro žáky */}
          {currentRole === "student" && (
            <>
              <Link to="/" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Přehled", "Prehľad")}
                </Button>
              </Link>
              <Link to="/tutor" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  AI Tutor
                </Button>
              </Link>
              <Link to="/challenges" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Výzvy a úkoly", "Výzvy a úlohy")}
                </Button>
              </Link>
              <Link to="/ideas" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  Moje nápady
                </Button>
              </Link>
              <Link to="/achievements" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Moje úspěchy", "Moje úspechy")}
                </Button>
              </Link>
              <Link to="/my-class" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Moje třída", "Moja trieda")}
                </Button>
              </Link>
            </>
          )}

          {/* Navigace pro učitele */}
          {currentRole === "teacher" && (
            <>
              <Link to="/teacher/classes" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Třídy", "Triedy")}
                </Button>
              </Link>
              <Link to="/teacher/challenges" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Výzvy a úkoly", "Výzvy a úlohy")}
                </Button>
              </Link>
              <Link to="/teacher/inspiration" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Inspirace", "Inšpirácie")}
                </Button>
              </Link>
            </>
          )}

          {/* Navigace pro administrátory */}
          {currentRole === "admin" && (
            <>
              <Link to="/admin/users" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Uživatelé", "Používatelia")}
                </Button>
              </Link>
              <Link to="/admin/testing" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Testování", "Testovanie")}
                </Button>
              </Link>
              <Link to="/admin/safety" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Bezpečnost", "Bezpečnosť")}
                </Button>
              </Link>
              <Link to="/admin/stats" onClick={handleLinkClick}>
                <Button variant="ghost" className="w-full justify-start">
                  {t("Statistiky", "Štatistiky")}
                </Button>
              </Link>
            </>
          )}

          <Link to="/hall-of-fame" onClick={handleLinkClick}>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start",
                isTopThree &&
                  "border border-yellow-500/60 bg-gradient-to-r from-yellow-500/15 via-amber-400/10 to-yellow-500/15 text-yellow-600 dark:text-yellow-400 hover:from-yellow-500/25 hover:via-amber-400/20 hover:to-yellow-500/25"
              )}
            >
              {isTopThree && <Trophy className="h-4 w-4 mr-2" />}
              {t("Síň slávy", "Sieň slávy")}
            </Button>
          </Link>

          {/* Role selector - only show if user has multiple allowed roles */}
          {!rolesLoading && allowedRoles.length > 1 && (
            <div className="border-t border-border pt-4 mt-4">
              <div className="flex items-center gap-2 px-4 mb-3 text-sm text-muted-foreground">
                <UserCog className="h-4 w-4" />
                {t("Přepnout roli", "Prepnúť rolu")}
              </div>
              <div className="flex flex-wrap gap-2 px-2">
                {allowedRoles.map((role) => (
                  <Button
                    key={role}
                    variant={currentRole === role ? "default" : "outline"}
                    size="sm"
                    className="flex-1 min-w-[80px] justify-center"
                    onClick={() => handleRoleChange(role)}
                    disabled={currentRole === role}
                  >
                    {getRoleLabel(role)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-border pt-4 mt-4 flex items-center justify-between px-2">
            <LanguageSelector />
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={toggleMode}
              aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {mode === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
